// src/components/PatientHistoryPanel.tsx
// Structured, directly-editable clinical history — Longitudinal Diagnoses
// (Year+Diagnosis), Past Procedures (Year+Procedure), Current Drugs
// (Name/Dose/Frequency). Seeded from the AI-generated Patient Timeline
// (rows tagged source='ai') but edited/added-to directly here thereafter —
// distinct from the Timeline itself, which stays read-only and AI-only.
// Reused editable in the Pre-Consult stage (and a template's history-table
// fields) and in Consultation's History tab (via MedicalHistoryTabs).
import { useState, useEffect, useCallback } from 'react';
import { patientDiagnoses, patientProcedures, patientMedications } from '../api';
import { Badge } from './ui/Badge';

export type HistoryKind = 'diagnoses' | 'procedures' | 'medications';

interface HistoryCol {
  key: string;
  label: string;
  width?: number;
  type: 'number' | 'text';
}

/** A row from any of the three history endpoints — loosely typed since the
 * column set differs per kind (year+diagnosis vs drug_name+dose+frequency)
 * and this component is generic over all three, same latitude the untyped
 * .jsx original took. */
export interface HistoryRowData {
  id: number;
  active?: boolean;
  source?: 'manual' | 'ai';
  [key: string]: unknown;
}

interface HistoryEndpointSet {
  list:   (uhid: string) => Promise<HistoryRowData[]>;
  create: (uhid: string, data: Record<string, unknown>) => Promise<unknown>;
  update: (uhid: string, id: number, data: Record<string, unknown>) => Promise<unknown>;
  remove: (uhid: string, id: number) => Promise<unknown>;
}

interface HistoryConfig {
  api: HistoryEndpointSet;
  title: string;
  /** One glyph identifying the kind at a glance in the row's leading tile —
   * same role as RailCard's own `tone` icons elsewhere in Consultation. */
  icon: string;
  accent: 'info' | 'caution' | 'success';
  cols: HistoryCol[];
  /** Which column carries the row's headline (bold, leading text) — the
   * rest render as one muted meta line underneath rather than as equal-
   * width columns, which is what made this read as a raw data table instead
   * of a clinical history list. */
  primaryKey: string;
  hasActiveToggle?: boolean;
  /** Sorted newest-first when every row has a parseable year — diagnoses/
   * procedures have no other ordering guarantee from the API, and a
   * chronological problem list is what a doctor scanning it expects.
   * Medications sort by their own Current/Past split instead (below). */
  sortByYear?: boolean;
}

const CONFIG: Record<HistoryKind, HistoryConfig> = {
  diagnoses: {
    api: patientDiagnoses, title: 'Diagnoses (problem list)', icon: '🩺', accent: 'info',
    cols: [{ key:'year', label:'Year', width:80, type:'number' }, { key:'diagnosis', label:'Diagnosis', type:'text' }],
    primaryKey: 'diagnosis', sortByYear: true,
  },
  procedures: {
    api: patientProcedures, title: 'Past procedures', icon: '🏥', accent: 'caution',
    cols: [{ key:'year', label:'Year', width:80, type:'number' }, { key:'procedure', label:'Procedure', type:'text' }],
    primaryKey: 'procedure', sortByYear: true,
  },
  medications: {
    api: patientMedications, title: 'Medications', icon: '💊', accent: 'success',
    cols: [{ key:'drug_name', label:'Drug', type:'text' }, { key:'dose', label:'Dose', width:100, type:'text' }, { key:'frequency', label:'Frequency', width:120, type:'text' }],
    primaryKey: 'drug_name',
    // Splits into Current/Past groups using the row's own `active` column
    // (patient_medications.active, migration_patient_history.sql) — the
    // column already existed but nothing surfaced it; every medication used
    // to render in one undifferentiated list regardless of whether it was
    // still being taken.
    hasActiveToggle: true,
  },
};

function emptyRow(cols: HistoryCol[]): Record<string, string> { return Object.fromEntries(cols.map(c => [c.key, ''])); }

function byYearDesc(a: HistoryRowData, b: HistoryRowData): number {
  const ay = Number(a.year), by = Number(b.year);
  if (Number.isNaN(ay) && Number.isNaN(by)) return 0;
  if (Number.isNaN(ay)) return 1;   // no-year rows sink to the bottom
  if (Number.isNaN(by)) return -1;
  return by - ay;
}

/** Buckets an already-newest-first list into one group per year, same
 * "year heading over its entries" shape PatientRecords.tsx's own Timeline
 * tab already uses for its AI-generated milestones — reused here so a
 * doctor scanning either screen gets the identical mental model for "what
 * happened when," rather than a differently-shaped view depending on which
 * screen they're on. */
function groupByYear(list: HistoryRowData[]): { year: number | null; rows: HistoryRowData[] }[] {
  const groups: { year: number | null; rows: HistoryRowData[] }[] = [];
  for (const row of list) {
    const y = Number(row.year);
    const year = Number.isNaN(y) ? null : y;
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.rows.push(row);
    else groups.push({ year, rows: [row] });
  }
  return groups;
}

export interface PatientHistoryPanelProps {
  kind: HistoryKind;
  uhid: string | undefined;
  editable?: boolean;
  compact?: boolean;
  /** Lets a parent (e.g. Pre-Consult, after an AI history pull) force a
   * reload without needing to know this component's internal state. */
  refreshToken?: unknown;
  /** Called after a row is added, edited or removed here, e.g. so a parent's counts stay current. */
  onChanged?: () => void;
}

export default function PatientHistoryPanel({ kind, uhid, editable = true, compact = false, refreshToken, onChanged }: PatientHistoryPanelProps) {
  const cfg = CONFIG[kind];
  const [rows, setRows] = useState<HistoryRowData[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>(emptyRow(cfg.cols));
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!uhid) return;
    cfg.api.list(uhid).then(setRows).catch((e: Error) => { setError(e.message); setRows([]); });
  }, [uhid]); // eslint-disable-line react-hooks/exhaustive-deps

  // refreshToken lets a parent (e.g. Pre-Consult, after an AI history pull)
  // force a reload without needing to know this component's internal state.
  useEffect(() => { load(); }, [load, refreshToken]);

  const save = async () => {
    if (!uhid) return;
    try {
      await cfg.api.create(uhid, draft);
      setDraft(emptyRow(cfg.cols)); setAdding(false);
      load(); onChanged?.();
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const updateRow = async (id: number, patch: Record<string, unknown>) => {
    if (!uhid) return;
    try { await cfg.api.update(uhid, id, patch); load(); onChanged?.(); } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const removeRow = async (id: number) => {
    if (!uhid) return;
    try { await cfg.api.remove(uhid, id); load(); onChanged?.(); } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const toggleActive = (row: HistoryRowData) => updateRow(row.id, { active: !row.active });

  if (!uhid) return null;

  // Medications split into Current/Past groups by their own `active` column
  // — every other kind (diagnoses, procedures) renders as one flat list,
  // sorted newest-first when it carries a year.
  const sortIfNeeded = (list: HistoryRowData[]) => cfg.sortByYear ? [...list].sort(byYearDesc) : list;
  const current = cfg.hasActiveToggle ? (rows || []).filter(r => r.active !== false) : sortIfNeeded(rows || []);
  const past     = cfg.hasActiveToggle ? (rows || []).filter(r => r.active === false) : [];

  return (
    <div style={{ marginBottom: compact ? 10 : 16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:11.5, fontWeight:700, letterSpacing:'.5px', color:'#6B7280', textTransform:'uppercase' }}>
          <span style={{ fontSize:13 }}>{cfg.icon}</span>{cfg.title}
        </div>
        {editable && !adding && <button className="btn-ghost" style={{ fontSize:11, padding:'2px 8px' }} onClick={()=>setAdding(true)}>+ Add</button>}
      </div>

      {error && <div style={{ fontSize:11, color:'#DC2626', marginBottom:6 }}>{error}</div>}

      {rows === null ? (
        <div style={{ fontSize:12, color:'#9CA3AF' }}>Loading…</div>
      ) : rows.length === 0 && !adding ? (
        <div style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>None recorded</div>
      ) : cfg.hasActiveToggle ? (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            {!compact && <div style={{ fontSize:10.5, fontWeight:700, color:'#059669', textTransform:'uppercase', marginBottom:5, letterSpacing:'.5px' }}>● Current</div>}
            {(current ?? []).length === 0
              ? <div style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>None recorded</div>
              : <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {(current ?? []).map(row => (
                    <HistoryRow key={row.id} row={row} cfg={cfg} editable={editable} onSave={patch => updateRow(row.id, patch)} onRemove={()=>removeRow(row.id)} onToggleActive={()=>toggleActive(row)} />
                  ))}
                </div>}
          </div>
          {past.length > 0 && (
            <div>
              <div style={{ fontSize:10.5, fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', marginBottom:5, letterSpacing:'.5px' }}>○ Past / discontinued</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {past.map(row => (
                  <HistoryRow key={row.id} row={row} cfg={cfg} editable={editable} onSave={patch => updateRow(row.id, patch)} onRemove={()=>removeRow(row.id)} onToggleActive={()=>toggleActive(row)} dimmed />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : cfg.sortByYear ? (
        // Year-grouped, same visual pattern as PatientRecords.tsx's own
        // Timeline tab — a bold year heading over its entries, rather than
        // each row repeating its own year. compact (Pre-Consult's
        // inline capture) keeps a tighter heading; the full History tab and
        // Patient Records get PatientRecords.tsx's own heading size.
        <div style={{ display:'flex', flexDirection:'column', gap: compact ? 10 : 14 }}>
          {groupByYear(current ?? []).map((g) => (
            <div key={g.year ?? 'undated'}>
              <div style={{
                display:'flex', alignItems:'baseline', gap:8, marginBottom:6,
                paddingBottom:5, borderBottom:'2px solid #F1F3F5',
              }}>
                <span style={{ fontSize: compact ? 14 : 17, fontWeight:800, color:'#111827' }}>{g.year ?? 'Undated'}</span>
                <span style={{ fontSize:11, color:'#9CA3AF' }}>{g.rows.length} {g.rows.length === 1 ? 'entry' : 'entries'}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {g.rows.map(row => (
                  <HistoryRow key={row.id} row={row} cfg={cfg} editable={editable} onSave={patch => updateRow(row.id, patch)} onRemove={()=>removeRow(row.id)} hideKeys={['year']} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {(current ?? []).map(row => (
            <HistoryRow key={row.id} row={row} cfg={cfg} editable={editable} onSave={patch => updateRow(row.id, patch)} onRemove={()=>removeRow(row.id)} />
          ))}
        </div>
      )}

      {adding && (
        <div style={{ display:'flex', gap:6, marginTop:8, alignItems:'center', flexWrap:'wrap' }}>
          {cfg.cols.map(c => (
            <input key={c.key} type={c.type} placeholder={c.label} value={draft[c.key] ?? ''}
              onChange={e=>setDraft(d=>({...d,[c.key]:e.target.value}))}
              style={{ fontSize:12, width: c.width || 140 }} />
          ))}
          <button className="btn-primary" style={{ fontSize:11, padding:'4px 10px' }} onClick={save}>Save</button>
          <button className="btn-ghost" style={{ fontSize:11, padding:'4px 10px' }} onClick={()=>{setAdding(false); setDraft(emptyRow(cfg.cols));}}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const ACCENT_COLORS: Record<HistoryConfig['accent'], { bg: string; fg: string }> = {
  info:     { bg: '#EFF6FF', fg: '#1D4ED8' },
  caution:  { bg: '#FAF5FF', fg: '#7E22CE' },
  success:  { bg: '#ECFDF5', fg: '#059669' },
};

interface HistoryRowProps {
  row: HistoryRowData;
  cfg: HistoryConfig;
  editable?: boolean;
  onSave: (patch: Record<string, unknown>) => void;
  onRemove: () => void;
  onToggleActive?: () => void;
  dimmed?: boolean;
  /** Columns already shown elsewhere (the year-group heading) and so
   * dropped from this row's own meta line — avoids "2023" printing twice. */
  hideKeys?: string[];
}

function HistoryRow({ row, cfg, editable, onSave, onRemove, onToggleActive, dimmed, hideKeys = [] }: HistoryRowProps) {
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<Record<string, unknown>>(row);

  if (editing) {
    return (
      <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', padding:'4px 8px' }}>
        {cfg.cols.map(c => (
          <input key={c.key} type={c.type} value={(vals[c.key] as string) ?? ''} onChange={e=>setVals(v=>({...v,[c.key]:e.target.value}))}
            placeholder={c.label} style={{ fontSize:12, width: c.width || 140 }} />
        ))}
        <button className="btn-primary" style={{ fontSize:11, padding:'3px 8px' }} onClick={()=>{ onSave(vals); setEditing(false); }}>Save</button>
        <button className="btn-ghost" style={{ fontSize:11, padding:'3px 8px' }} onClick={()=>setEditing(false)}>Cancel</button>
      </div>
    );
  }

  // Everything except the headline column becomes one muted "· "-joined
  // meta line underneath it (e.g. medication → "500mg · OD", diagnosis →
  // "2023") instead of a row of equal-width columns — reading this used to
  // mean parsing a raw table; a headline + subtext is what a chart problem
  // list / medication list actually looks like.
  const primary = (row[cfg.primaryKey] as string) || '—';
  const meta = cfg.cols
    .filter(c => c.key !== cfg.primaryKey && !hideKeys.includes(c.key))
    .map(c => row[c.key])
    .filter(v => v != null && v !== '')
    .join(' · ');
  const colors = ACCENT_COLORS[cfg.accent];

  return (
    <div style={{
      display:'flex', gap:10, alignItems:'flex-start', padding:'7px 10px',
      background: dimmed ? '#F9FAFB' : '#fff', border: '1px solid #F1F3F5', borderRadius:8,
      opacity: dimmed ? .72 : 1,
    }}>
      <div style={{
        flexShrink:0, width:26, height:26, borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:13, background: colors.bg,
      }}>
        {cfg.icon}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:'#1F2937', lineHeight:1.3 }}>{primary}</div>
        {meta && <div style={{ fontSize:11.5, color:'#6B7280', marginTop:1 }}>{meta}</div>}
      </div>
      {row.source === 'ai' && <Badge variant="info">AI</Badge>}
      {editable && (
        <div style={{ display:'flex', gap:4, flexShrink:0 }}>
          {onToggleActive && (
            <button className="btn-ghost" style={{ fontSize:11, padding:'2px 6px' }} onClick={onToggleActive}>
              {row.active === false ? '↺ Reactivate' : '⏹ Discontinue'}
            </button>
          )}
          <button className="btn-ghost" style={{ fontSize:11, padding:'2px 6px' }} onClick={()=>setEditing(true)}>Edit</button>
          <button className="btn-ghost" style={{ fontSize:11, padding:'2px 6px', color:'#DC2626' }} onClick={onRemove}>✕</button>
        </div>
      )}
    </div>
  );
}
